import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Card, 
  CardContent, 
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import { eventsApi } from '../../services/api/events.api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`events-tabpanel-${index}`}
      aria-labelledby={`events-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const EventsApiTest: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<Record<string, any>>({});
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [firstEventId, setFirstEventId] = useState<string | null>(null);

  const testEndpoint = async (name: string, testFn: () => Promise<any>) => {
    setLoading(true);
    setError(null);
    try {
      const result = await testFn();
      setResults(prev => ({ ...prev, [name]: { success: true, data: result } }));
    } catch (err: any) {
      setResults(prev => ({ 
        ...prev, 
        [name]: { 
          success: false, 
          error: err.message || 'Неизвестная ошибка' 
        } 
      }));
      setError(`${name}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Управление событиями
  const testGetEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await eventsApi.getEvents();
      setResults(prev => ({ ...prev, 'Get Events': { success: true, data: result } }));
      // Сохраняем ID первого события для тестирования Get Event
      let eventsArray: unknown[] | null = null;
      if (Array.isArray(result)) {
        eventsArray = result;
      } else if (result && typeof result === 'object' && 'data' in result) {
        if (Array.isArray((result as any).data)) {
          eventsArray = (result as any).data;
        } else if ((result as any).data && typeof (result as any).data === 'object' && 'events' in (result as any).data) {
          if (Array.isArray((result as any).data.events)) {
            eventsArray = (result as any).data.events;
          }
        }
      } else if (result && typeof result === 'object' && 'events' in result && Array.isArray((result as any).events)) {
        eventsArray = (result as any).events;
      }
      
      if (eventsArray && eventsArray.length > 0) {
        const first = eventsArray[0] as { id?: string };
        if (first?.id) setFirstEventId(first.id);
      }
    } catch (err: any) {
      setResults(prev => ({ 
        ...prev, 
        'Get Events': { 
          success: false, 
          error: err.message || 'Неизвестная ошибка' 
        } 
      }));
      setError(`Get Events: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  const testGetEvent = async () => {
    if (!firstEventId) {
      setError('Нет доступных событий для тестирования. Сначала создайте событие в системе, затем выполните тест Get Events.');
      return;
    }
    await testEndpoint('Get Event', () => eventsApi.getEvent(firstEventId));
  };
  
  const testGetEventStats = () => testEndpoint('Get Event Stats', () => eventsApi.getEventStats());
  const testGetEventAnalytics = () => testEndpoint('Get Event Analytics', () => eventsApi.getEventAnalytics());

  // Категории и теги (обновлено согласно рекомендациям бекенда)
  const testGetEventCategories = () => testEndpoint('Get Event Categories', () => eventsApi.getEventCategories());
  const testGetEventTags = () => testEndpoint('Get Event Types', () => eventsApi.getEventTags());
  const testSearchEvents = () => testEndpoint('Search Events', () => eventsApi.searchEvents('test'));

  // Предстоящие и прошедшие события
  const testGetUpcomingEvents = () => testEndpoint('Get Upcoming Events', () => eventsApi.getUpcomingEvents());
  const testGetPastEvents = () => testEndpoint('Get Past Events', () => eventsApi.getPastEvents());

  // Участники событий
  const testGetEventParticipants = () => {
    if (!firstEventId) {
      setError('Сначала выполните тест Get Events для получения ID события');
      return;
    }
    return testEndpoint('Get Event Participants', () => 
      eventsApi.getEventParticipants(firstEventId)
    );
  };

  // Регистрация
  const testGetRegistrationStatus = () => {
    if (!firstEventId) {
      setError('Сначала выполните тест Get Events для получения ID события');
      return;
    }
    return testEndpoint('Get Registration Status', () => 
      eventsApi.getRegistrationStatus(firstEventId)
    );
  };

  const testAllManagementEndpoints = async () => {
    await testGetEvents();
    await testGetEvent(); // Теперь использует реальный ID события
    await testGetEventStats();
    await testGetEventAnalytics();
  };

  const testAllCategoriesEndpoints = async () => {
    await testGetEventCategories();
    await testGetEventTags();
    await testSearchEvents();
  };

  const testAllTimeEndpoints = async () => {
    await testGetUpcomingEvents();
    await testGetPastEvents();
  };

  const testAllParticipantsEndpoints = async () => {
    await testGetEventParticipants();
    await testGetRegistrationStatus();
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const clearResults = () => {
    setResults({});
    setError(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        📅 Тестирование Events API
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Тестирование 30 endpoints из Events модуля
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="events test tabs">
          <Tab label="Управление событиями" />
          <Tab label="Категории и теги" />
          <Tab label="Временные события" />
          <Tab label="Участники и регистрация" />
          <Tab label="Все результаты" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item>
            <Button 
              variant="contained" 
              onClick={testAllManagementEndpoints}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              Тестировать все endpoints управления
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              onClick={testGetEvents}
              disabled={loading}
            >
              Тест Get Events
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              onClick={testGetEvent}
              disabled={loading}
            >
              Тест Get Event
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              onClick={testGetEventStats}
              disabled={loading}
            >
              Тест Event Stats
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              onClick={testGetEventAnalytics}
              disabled={loading}
            >
              Тест Event Analytics
            </Button>
          </Grid>
        </Grid>

        {/* Отображение результатов для вкладки управления событиями */}
        <Divider sx={{ my: 2 }} />
        <Grid container spacing={2}>
          {Object.entries(results).filter(([name]) => 
            ['Get Events', 'Get Event', 'Get Event Stats', 'Get Event Analytics'].includes(name)
          ).map(([name, result]) => (
            <Grid item xs={12} md={6} key={name}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {name}
                  </Typography>
                  {result.success ? (
                    <Alert severity="success">
                      ✅ Успешно
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {JSON.stringify(result.data, null, 2)}
                      </Typography>
                    </Alert>
                  ) : (
                    <Alert severity="error">
                      ❌ Ошибка: {result.error}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {Object.keys(results).filter(name => 
          ['Get Events', 'Get Event', 'Get Event Stats', 'Get Event Analytics'].includes(name)
        ).length === 0 && (
          <Card>
            <CardContent>
              <Typography variant="body1" color="text.secondary" align="center">
                Нажмите кнопку для начала тестирования
              </Typography>
            </CardContent>
          </Card>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item>
            <Button 
              variant="contained" 
              onClick={testAllCategoriesEndpoints}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              Тестировать все endpoints категорий
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              onClick={testGetEventCategories}
              disabled={loading}
            >
              Тест Get Categories
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              onClick={testGetEventTags}
              disabled={loading}
            >
              Тест Get Event Types
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              onClick={testSearchEvents}
              disabled={loading}
            >
              Тест Search Events
            </Button>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item>
            <Button 
              variant="contained" 
              onClick={testAllTimeEndpoints}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              Тестировать все временные endpoints
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              onClick={testGetUpcomingEvents}
              disabled={loading}
            >
              Тест Upcoming Events
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              onClick={testGetPastEvents}
              disabled={loading}
            >
              Тест Past Events
            </Button>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item>
            <Button 
              variant="contained" 
              onClick={testAllParticipantsEndpoints}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              Тестировать все endpoints участников
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              onClick={testGetEventParticipants}
              disabled={loading}
            >
              Тест Get Participants
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              onClick={testGetRegistrationStatus}
              disabled={loading}
            >
              Тест Registration Status
            </Button>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <Box sx={{ mb: 3 }}>
          <Button 
            variant="outlined" 
            onClick={clearResults}
            disabled={loading}
          >
            Очистить результаты
          </Button>
        </Box>

        <Grid container spacing={2}>
          {Object.entries(results).map(([name, result]) => (
            <Grid item xs={12} md={6} key={name}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {name}
                  </Typography>
                  {result.success ? (
                    <Alert severity="success">
                      ✅ Успешно
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {JSON.stringify(result.data, null, 2)}
                      </Typography>
                    </Alert>
                  ) : (
                    <Alert severity="error">
                      ❌ Ошибка: {result.error}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {Object.keys(results).length === 0 && (
          <Card>
            <CardContent>
              <Typography variant="body1" color="text.secondary" align="center">
                Нажмите кнопку для начала тестирования
              </Typography>
            </CardContent>
          </Card>
        )}
      </TabPanel>
    </Box>
  );
};

export default EventsApiTest;
